import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { changePasswordSchema, type ChangePasswordInput } from '@marketing-os/shared';
import { applyApiErrors } from '@/lib/form-errors';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { useChangePassword } from './api';

export function ChangePasswordPage() {
  const toast = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const changePassword = useChangePassword();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordInput>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await changePassword.mutateAsync(values);
      toast.success('Senha alterada', 'Use a nova senha no próximo acesso.');
      reset();
    } catch (error) {
      setFormError(
        applyApiErrors(error, setError, ['currentPassword', 'newPassword', 'confirmPassword']),
      );
    }
  });

  return (
    <>
      <PageHeader
        title="Alterar senha"
        description="A nova senha precisa ter no mínimo 10 caracteres, com letra maiúscula, minúscula e número."
      />

      <Card className="max-w-lg">
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            {formError && <Alert tone="danger">{formError}</Alert>}

            <Field label="Senha atual" error={errors.currentPassword?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('currentPassword')}
                />
              )}
            </Field>

            <Field label="Nova senha" error={errors.newPassword?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('newPassword')}
                />
              )}
            </Field>

            <Field label="Confirmar nova senha" error={errors.confirmPassword?.message} required>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="new-password"
                  aria-describedby={describedBy}
                  invalid={invalid}
                  {...register('confirmPassword')}
                />
              )}
            </Field>

            <Button
              type="submit"
              loading={isSubmitting || changePassword.isPending}
              leftIcon={<KeyRound className="h-4 w-4" />}
            >
              Salvar nova senha
            </Button>
          </form>
        </CardBody>
      </Card>
    </>
  );
}
